import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function TableShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface)] shadow-sm',
        className
      )}
    >
      {children}
    </div>
  );
}

export function TableScroll({
  children,
  minWidth = 'min-w-[48rem]'
}: {
  children: ReactNode;
  /**
   * Tailwind min-width class for the table. The default suits wide listing
   * tables; pass `min-w-0` when every column is fixed-width except a truncating
   * one, so the table fits its column instead of forcing a scrollbar.
   */
  minWidth?: string;
}) {
  return (
    <div className="scroll-thin w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', minWidth)}>{children}</table>
    </div>
  );
}

export function Th({
  children,
  className,
  align = 'left',
  width
}: {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
}) {
  return (
    <th
      scope="col"
      style={{ width }}
      className={cn(
        'border-b border-[var(--line)] bg-[var(--surface-2)] px-4 py-2.5 text-sm font-medium text-[var(--text-muted)]',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className
      )}
    >
      {children}
    </th>
  );
}

export function SortableTh<T extends string>({
  field,
  active,
  order,
  onSort,
  children,
  align = 'left',
  width
}: {
  field: T;
  active: T | null;
  order: 'asc' | 'desc';
  /** Cycles asc -> desc -> unsorted, matching v1's behavior. */
  onSort: (field: T | null, order: 'asc' | 'desc') => void;
  children: ReactNode;
  align?: 'left' | 'right';
  width?: string;
}) {
  const isActive = active === field;
  const next = () => {
    if (!isActive) return onSort(field, 'asc');
    if (order === 'asc') return onSort(field, 'desc');
    return onSort(null, 'desc');
  };

  const Icon = !isActive ? ChevronsUpDown : order === 'asc' ? ArrowUp : ArrowDown;

  return (
    <Th align={align} width={width} className="p-0">
      <button
        type="button"
        onClick={next}
        aria-sort={isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn(
          'flex w-full items-center gap-1.5 px-4 py-2.5 font-medium transition-colors hover:text-[var(--text)]',
          align === 'right' && 'justify-end',
          isActive && 'text-[var(--text)]'
        )}
      >
        {children}
        <Icon className={cn('size-3.5', !isActive && 'opacity-40')} />
      </button>
    </Th>
  );
}

export function Td({
  children,
  className,
  align = 'left',
  colSpan,
  onMouseEnter
}: {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
  colSpan?: number;
  onMouseEnter?: () => void;
}) {
  return (
    <td
      colSpan={colSpan}
      onMouseEnter={onMouseEnter}
      className={cn(
        'px-4 py-2.5 align-middle',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className
      )}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  className,
  pending,
  onClick,
  style
}: {
  children: ReactNode;
  className?: string;
  /** Faintly dims a row whose optimistic write is still in flight. */
  pending?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <tr
      onClick={onClick}
      style={style}
      className={cn(
        'border-b border-[var(--line)] transition-colors last:border-0',
        onClick && 'cursor-pointer',
        'hover:bg-[var(--surface-hover)]',
        pending && 'is-pending',
        className
      )}
    >
      {children}
    </tr>
  );
}

/** Skeleton rows shaped like the real table so nothing shifts on load. */
export function SkeletonRow({ widths }: { widths: (string | null)[] }) {
  return (
    <tr className="border-b border-[var(--line)] last:border-0">
      {widths.map((w, i) => (
        <td key={i} className="px-4 py-3">
          {w ? <div className="skeleton h-4" style={{ width: w }} /> : null}
        </td>
      ))}
    </tr>
  );
}
